import { useCallback } from 'react';
import { loadImageSafely } from '../utils/loadImageSafely';

// @vladmandic/face-api ships its model weights inside the npm package itself
// (under /model), unlike the original face-api.js which required a separate
// weights repo — jsdelivr's npm mirror serves that folder directly. Pinned to
// the exact installed version so a fork release can't silently change models
// out from under us.
const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.15/model';

// Face *recognition/matching* (computing a per-person facial-geometry
// embedding used to identify someone) was removed entirely — that's the
// exact activity biometric-privacy laws like Illinois' BIPA regulate,
// regardless of whether the matching ran client- or server-side, and it
// doesn't stop being regulated just because the account holder consented —
// BIPA requires consent from the person *whose face it is*, not whoever
// uploaded the photo, which most of this app's tagged subjects (children,
// deceased relatives, non-user friends/family) can never practically give.
// Only face *detection* (finding that a face exists, for crop targets and
// quality scoring) remains. See the project's face-detection redesign notes
// for the full rationale.

export interface DetectedFace {
  cropUrl: string;
  /** 0-1, how open the eyes appear (eye-aspect-ratio derived) — 1 = clearly open. */
  eyesOpenScore: number;
  /** 0-1, this face's bounding-box area relative to the whole photo. */
  faceAreaRatio: number;
}

let faceapiPromise: Promise<typeof import('@vladmandic/face-api')> | null = null;
function getFaceApi() {
  if (!faceapiPromise) faceapiPromise = import('@vladmandic/face-api');
  return faceapiPromise;
}

// @vladmandic/face-api bundles its own internal copy of tfjs-core (not a
// separate npm dependency — `faceapi.tf` re-exports that exact instance), so
// backend selection has to go through `faceapi.tf`, not a standalone
// `@tensorflow/tfjs-backend-*` package (which would register against a
// different engine instance and never actually be used).
//
// Found via a live browser console: in some environments tfjs's automatic
// backend auto-selection tries 'wasm' (fetching a .wasm binary that this
// app doesn't serve at the path tfjs expects — it 404s into the SPA's
// index.html fallback, a MIME-type mismatch) and 'webgl' (unsupported on
// some devices/browsers), then simply gives up without ever trying 'cpu' —
// even though a 'cpu' backend IS bundled and registered (confirmed by
// inspecting the shipped bundle). Every detectFaces() call then throws
// "Backend 'undefined' has not yet been initialized," which the caller's
// catch collapses into a silent "0 faces found," indistinguishable from a
// genuinely faceless photo. Explicitly trying backends ourselves (webgl
// first for speed, cpu as a guaranteed pure-JS fallback with no GPU/wasm
// dependency) sidesteps whatever is wrong with the automatic selection.
// The shipped face-api.d.ts only declares tf's tensor ops/types, not its
// engine-control methods — but they're genuinely present on the runtime
// object (confirmed by inspecting the built bundle for `setBackend:`,
// `getBackend:`, `ready:`, `registerBackend:` property names), so this
// narrow interface fills the type-declaration gap rather than reaching for
// a blanket `any`.
interface TfEngineControls {
  setBackend(name: string): Promise<boolean>;
  getBackend(): string;
  ready(): Promise<void>;
}

let backendReady: Promise<void> | null = null;
async function ensureBackend(faceapi: Awaited<ReturnType<typeof getFaceApi>>) {
  if (!backendReady) {
    backendReady = (async () => {
      const tf = faceapi.tf as unknown as TfEngineControls;
      for (const backend of ['webgl', 'cpu']) {
        try {
          await tf.setBackend(backend);
          await tf.ready();
          if (tf.getBackend() === backend) return;
        } catch (err) {
          console.warn(`Face detection: '${backend}' backend failed to initialize, trying next`, err);
        }
      }
      console.error('Face detection: no working backend could be initialized (tried webgl, cpu)');
    })();
  }
  return backendReady;
}

let modelsLoaded = false;
async function ensureModels() {
  const faceapi = await getFaceApi();
  await ensureBackend(faceapi);
  if (modelsLoaded) return faceapi;
  await Promise.all([
    faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
    faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
  ]);
  modelsLoaded = true;
  return faceapi;
}

// CORS-safe image loading (R2 has no CORS policy, so a raw cross-origin
// <img>/canvas read silently taints) lives in utils/loadImageSafely.ts —
// shared with Collage Creator's canvas rendering rather than duplicated here.
const loadImage = loadImageSafely;

function cropRegion(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  box: { x: number; y: number; width: number; height: number }
): string {
  const pad = 0.35;
  const pw = box.width * pad;
  const ph = box.height * pad;
  const x = Math.max(0, box.x - pw);
  const y = Math.max(0, box.y - ph);
  const w = Math.min(sourceWidth - x, box.width + pw * 2);
  const h = Math.min(sourceHeight - y, box.height + ph * 2);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d')!.drawImage(source, x, y, w, h, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.85);
}

// TinyFaceDetector already internally resizes its input to a fixed size
// regardless of source resolution, so this doesn't meaningfully speed up
// the model itself — what it does save is the browser's own decode/paint/GC
// cost of repeatedly drawing full phone-camera-resolution photos (often
// 3000-4000px+) across a long sequential scan of many photos in one sitting.
// Only used when a caller opts in via detectFaces's maxDimension option; the
// default (untouched) path is unaffected.
function downscaleToCanvas(img: HTMLImageElement, maxDimension: number): HTMLCanvasElement {
  const largestSide = Math.max(img.naturalWidth, img.naturalHeight);
  const scale = Math.min(1, maxDimension / largestSide);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

interface Point { x: number; y: number }

function dist(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

// Eye-aspect-ratio (EAR): (|p2-p6| + |p3-p5|) / (2|p1-p4|) over the 6-point
// eye contour face-api's landmark model returns. Open eyes land ~0.25-0.35;
// closed eyes drop well below ~0.15. Normalized to a 0-1 "openness" score.
function eyeAspectRatio(eye: Point[]): number {
  if (eye.length < 6) return 0.5;
  const vertical = dist(eye[1], eye[5]) + dist(eye[2], eye[4]);
  const horizontal = dist(eye[0], eye[3]);
  if (horizontal === 0) return 0.5;
  return vertical / (2 * horizontal);
}

function eyesOpenScore(landmarks: { getLeftEye(): Point[]; getRightEye(): Point[] }): number {
  const ear = (eyeAspectRatio(landmarks.getLeftEye()) + eyeAspectRatio(landmarks.getRightEye())) / 2;
  return Math.max(0, Math.min(1, (ear - 0.1) / 0.2));
}

export function preloadFaceModels(): void {
  ensureModels().catch(() => {});
}

export function useFaceDetection() {
  const detectFaces = useCallback(async (blobUrl: string, opts?: { maxDimension?: number }): Promise<DetectedFace[]> => {
    try {
      const faceapi = await ensureModels();
      const img = await loadImage(blobUrl);
      const source: CanvasImageSource = opts?.maxDimension ? downscaleToCanvas(img, opts.maxDimension) : img;
      const sourceWidth = source instanceof HTMLCanvasElement ? source.width : img.naturalWidth;
      const sourceHeight = source instanceof HTMLCanvasElement ? source.height : img.naturalHeight;

      const detections = await faceapi
        .detectAllFaces(source, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks(true);

      if (!detections.length) return [];

      const imageArea = sourceWidth * sourceHeight;

      return detections.map(det => {
        const cropUrl = cropRegion(source, sourceWidth, sourceHeight, det.detection.box);
        const { width, height } = det.detection.box;
        return {
          cropUrl,
          eyesOpenScore: eyesOpenScore(det.landmarks),
          faceAreaRatio: imageArea > 0 ? (width * height) / imageArea : 0,
        };
      });
    } catch (err) {
      // Every failure here (model load, CORS/proxy fetch, detection itself)
      // used to collapse into a silent "0 faces found" with no way to tell
      // "genuinely no faces in this photo" from "the pipeline is broken" —
      // which cost multiple rounds of guessing on a prior bug. Logging the
      // real error means the next failure is diagnosable from DevTools
      // console directly instead of needing another screen recording.
      console.error('detectFaces failed', err);
      return [];
    }
  }, []);

  // Manual fallback for photos where auto-detection finds nothing at all —
  // small-in-frame, side-angle, sunglasses/hat, or otherwise hard faces the
  // full-photo detector misses. The user taps roughly where the face is; we
  // zoom into a region around that point (making the face a much larger
  // fraction of the input) and re-run the *same* detection pipeline on just
  // that crop, which succeeds surprisingly often even when the full-frame
  // pass didn't. Returns null (not a thrown error) if no face is found even
  // zoomed in, so the caller can ask the user to try again.
  const describeFaceAtPoint = useCallback(async (blobUrl: string, xNorm: number, yNorm: number): Promise<DetectedFace | null> => {
    try {
      const faceapi = await ensureModels();
      const img = await loadImage(blobUrl);

      const regionSize = Math.min(img.naturalWidth, img.naturalHeight) * 0.4;
      const cx = xNorm * img.naturalWidth;
      const cy = yNorm * img.naturalHeight;
      const rx = Math.max(0, Math.min(img.naturalWidth - regionSize, cx - regionSize / 2));
      const ry = Math.max(0, Math.min(img.naturalHeight - regionSize, cy - regionSize / 2));
      const rw = Math.min(regionSize, img.naturalWidth - rx);
      const rh = Math.min(regionSize, img.naturalHeight - ry);

      const regionCanvas = document.createElement('canvas');
      regionCanvas.width = rw;
      regionCanvas.height = rh;
      regionCanvas.getContext('2d')!.drawImage(img, rx, ry, rw, rh, 0, 0, rw, rh);

      const det = await faceapi
        .detectSingleFace(regionCanvas, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks(true);
      if (!det) return null;

      const regionArea = rw * rh;
      const { width, height } = det.detection.box;
      return {
        cropUrl: cropRegion(regionCanvas, rw, rh, det.detection.box),
        eyesOpenScore: eyesOpenScore(det.landmarks),
        faceAreaRatio: regionArea > 0 ? (width * height) / regionArea : 0,
      };
    } catch (err) {
      console.error('describeFaceAtPoint failed', err);
      return null;
    }
  }, []);

  return { detectFaces, describeFaceAtPoint };
}
